import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { AlertTriangle, Info, XOctagon, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLE = {
  info:     { icon: Info,          cls: 'bg-blue-500/15 border-blue-500/40 text-blue-200' },
  warning:  { icon: AlertTriangle, cls: 'bg-amber-500/20 border-amber-500/50 text-amber-100' },
  critical: { icon: XOctagon,      cls: 'bg-red-500/20 border-red-500/50 text-red-100' },
};

const DISMISS_KEY = 'yvf.broadcast.dismissed';

const readDismissed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]')); }
  catch (e) {
    console.debug('BroadcastBanner: could not parse dismissed list, resetting', e?.message);
    return new Set();
  }
};

// Sticky top-of-page banner that surfaces master-admin broadcasts
// (typically pre-deploy warnings). Polls every 30s. Anyone can dismiss
// locally; a new notification appears at once.
export const BroadcastBanner = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/notifications/active`, { headers });
        if (!cancelled) setNotifications(r.data?.notifications || []);
      } catch (err) {
        console.debug('BroadcastBanner poll failed', err?.message);
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token, headers]);

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(next))); } catch (e) {
      console.debug('BroadcastBanner: could not persist dismissal', e?.message);
    }
  };

  const visible = notifications.filter(n => !dismissed.has(n.id));
  if (visible.length === 0) return null;

  return (
    <div className="sticky top-0 z-40 space-y-1" data-testid="broadcast-banner">
      {visible.map(n => {
        const style = SEVERITY_STYLE[n.severity] || SEVERITY_STYLE.warning;
        const Icon = style.icon;
        return (
          <div
            key={n.id}
            className={`flex items-center gap-3 px-4 py-2.5 border-b ${style.cls} shadow-sm`}
            data-testid={`broadcast-${n.id}`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm font-medium flex-1 truncate">{n.message}</p>
            <span className="text-xs opacity-70 hidden sm:inline">
              until {new Date(n.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            <button
              onClick={() => dismiss(n.id)}
              className="opacity-60 hover:opacity-100 flex-shrink-0"
              aria-label="Dismiss notification"
              data-testid={`broadcast-dismiss-${n.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default BroadcastBanner;
